/**
 * 0.26.0 — Payment activity (every step of an online ACH payment, not only the cleared ledger
 * post). Shared by the API (webhook / portal / owner routes) and the SPA (owner panel, dashboard
 * card, tenant portal) so status names, plain-language copy, the settlement ESTIMATE and the list
 * of Stripe webhook events the operator must enable can never drift between them.
 */
import { z } from 'zod';

/** Attempt status — mirrors the Stripe PaymentIntent lifecycle (see packages/db schema). */
export type PaymentAttemptStatus =
  | 'created'
  | 'requires_payment_method'
  | 'requires_confirmation'
  | 'requires_action'
  | 'processing'
  | 'succeeded'
  | 'payment_failed'
  | 'canceled'
  | 'reversed';

export type PaymentAttemptKind = 'rent' | 'move_in' | 'other';

/** Not yet submitted — the tenant opened the pay page but no bank debit exists. */
export const OPEN_ATTEMPT_STATUSES: readonly PaymentAttemptStatus[] = [
  'created',
  'requires_payment_method',
  'requires_confirmation',
];

/** Final at Stripe — a PaymentIntent never leaves these (reversal is a ledger-side follow-on). */
export const TERMINAL_ATTEMPT_STATUSES: readonly PaymentAttemptStatus[] = ['succeeded', 'canceled', 'reversed'];

/**
 * The Stripe webhook events the endpoint (Developers → Webhooks → endpoint → Select events) MUST be
 * subscribed to for the payment-activity ledger to be complete. The first six drive the attempt
 * lifecycle; `payment_intent.succeeded` also posts the ledger payment; the last three are the
 * pre-0.26.0 ACH reversal (return / refund / dispute) events.
 */
export const REQUIRED_STRIPE_WEBHOOK_EVENTS = [
  'payment_intent.created',
  'payment_intent.requires_action',
  'payment_intent.processing',
  'payment_intent.succeeded',
  'payment_intent.payment_failed',
  'payment_intent.canceled',
  'charge.refunded',
  'charge.dispute.created',
  'charge.dispute.funds_withdrawn',
] as const;

/** Business days an ACH debit usually takes to settle — the basis of the ESTIMATE only. */
export const ACH_SETTLEMENT_BUSINESS_DAYS = 4;

/**
 * ESTIMATED settlement date (yyyy-mm-dd, UTC calendar): `fromIso` + `days` business days
 * (Mon–Fri; bank holidays are NOT modelled — always shown as an estimate, never a promise).
 */
export function addBusinessDays(fromIso: string, days = ACH_SETTLEMENT_BUSINESS_DAYS): string | null {
  const d = new Date(fromIso);
  if (Number.isNaN(d.getTime())) return null;
  const cur = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  let added = 0;
  while (added < days) {
    cur.setUTCDate(cur.getUTCDate() + 1);
    const dow = cur.getUTCDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return cur.toISOString().slice(0, 10);
}

/** Short owner-facing status label (badge text — never color alone). */
export function attemptStatusLabel(s: PaymentAttemptStatus): string {
  switch (s) {
    case 'created':
      return 'Started — not submitted';
    case 'requires_payment_method':
      return 'Open — no bank chosen';
    case 'requires_confirmation':
      return 'Bank chosen — not confirmed';
    case 'requires_action':
      return 'Waiting on tenant (verification)';
    case 'processing':
      return 'Processing — money in flight';
    case 'succeeded':
      return 'Paid (cleared)';
    case 'payment_failed':
      return 'Failed';
    case 'canceled':
      return 'Canceled';
    case 'reversed':
      return 'Reversed (returned/refunded)';
  }
}

/**
 * Plain-language reason for a failed ACH debit / verification, keyed on the Stripe error / ACH
 * return code. Never echoes bank data. Unknown codes fall back to a neutral sentence.
 */
export function plainFailureReason(code: string | null | undefined): string {
  switch (code) {
    case 'insufficient_funds':
      return 'Your bank returned the payment for insufficient funds.';
    case 'account_closed':
    case 'bank_account_closed':
      return 'Your bank says this account is closed.';
    case 'no_account':
    case 'invalid_account_number':
    case 'bank_account_invalid':
      return 'Your bank could not find this account.';
    case 'debit_not_authorized':
    case 'bank_account_restricted':
    case 'account_frozen':
      return 'Your bank did not allow this debit from the account.';
    case 'bank_account_unusable':
    case 'bank_account_declined':
      return 'This bank account cannot be used for payments.';
    case 'payment_method_microdeposit_verification_amounts_mismatch':
    case 'payment_method_microdeposit_verification_descriptor_code_mismatch':
      return 'The bank verification amounts or code did not match.';
    case 'payment_method_microdeposit_verification_attempts_exceeded':
      return 'Too many bank verification attempts were made.';
    case 'payment_method_microdeposit_verification_timeout':
      return 'The bank account was not verified in time.';
    default:
      return 'Your bank payment did not go through.';
  }
}

/** One history row of an attempt (owner/staff view). */
export interface PaymentAttemptEventDTO {
  id: string;
  type: string;
  source: 'stripe' | 'portal' | 'client' | 'owner' | 'tenant' | 'sweep' | 'sync';
  statusAfter: PaymentAttemptStatus | null;
  occurredAt: string;
  stripeEventId: string | null;
}

/** Owner/staff projection — everything except the tenant-only verification URL. */
export interface PaymentAttemptDTO {
  id: string;
  leaseId: string;
  tenantId: string | null;
  tenantName: string | null;
  unitLabel: string | null;
  propertyName: string | null;
  stripePaymentIntentId: string;
  /** https://dashboard.stripe.com/payments/<pi> */
  stripeDashboardUrl: string;
  amountCents: number;
  currency: string;
  kind: PaymentAttemptKind;
  status: PaymentAttemptStatus;
  statusLabel: string;
  statusSource: 'stripe' | 'client' | 'portal';
  origin: 'portal' | 'webhook' | 'sync';
  nextActionType: string | null;
  verificationArrivalAt: string | null;
  paymentMethodType: string | null;
  bankName: string | null;
  bankLast4: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  failureReason: string | null;
  cancellationReason: string | null;
  startedAt: string;
  initiatedAt: string | null;
  /** ESTIMATE — initiated + 4 business days. */
  expectedSettlementDate: string | null;
  succeededAt: string | null;
  failedAt: string | null;
  canceledAt: string | null;
  reversedAt: string | null;
  ledgerEntryId: string | null;
  /** Succeeded at Stripe but no matching ledger payment was found (should self-heal via webhook). */
  ledgerPostMissing: boolean;
  updatedAt: string;
  events: PaymentAttemptEventDTO[];
}

export interface PaymentAttemptListResponse {
  attempts: PaymentAttemptDTO[];
}

/** Owner dashboard card: money in flight + abandoned attempts. */
export interface PaymentActivitySummaryDTO {
  /** `processing` — the bank debit is submitted, not yet cleared. */
  processing: { count: number; totalCents: number; latestExpectedSettlementDate: string | null };
  /** `requires_action` — waiting on the tenant (e.g. microdeposit verification); no money moved. */
  requiresAction: { count: number; totalCents: number };
  /** Started (pay page opened) but not submitted, and idle for over an hour. */
  abandoned: { count: number; totalCents: number };
  /** Failed in the last 30 days. */
  failedRecent: { count: number };
  inFlight: PaymentAttemptDTO[];
}

/** Tenant projection (their OWN attempts only) — plain-language, no Stripe ids. */
export interface TenantPaymentAttemptDTO {
  id: string;
  amountCents: number;
  kind: PaymentAttemptKind;
  status: PaymentAttemptStatus;
  startedAt: string;
  initiatedAt: string | null;
  expectedSettlementDate: string | null;
  succeededAt: string | null;
  failedAt: string | null;
  canceledAt: string | null;
  bankName: string | null;
  bankLast4: string | null;
  failureReason: string | null;
  nextActionType: string | null;
  verificationArrivalAt: string | null;
  /** Stripe-hosted microdeposit verification page, while `requires_action`. */
  verificationUrl: string | null;
  /** Reported by the browser, awaiting Stripe confirmation. */
  awaitingConfirmation: boolean;
  /** Stripe says succeeded AND the ledger payment is posted. `succeeded` without it = "posting". */
  postedToLedger: boolean;
  canContinue: boolean;
  canCancel: boolean;
}

export interface TenantPaymentAttemptListResponse {
  attempts: TenantPaymentAttemptDTO[];
  /** Sum of the lease's `processing` attempts — shown as "pending", never as paid. */
  pendingPaymentCents: number;
}

export interface PaymentAttemptSyncResponse {
  scanned: number;
  matched: number;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
}

export interface StripeWebhookStatusDTO {
  configured: boolean;
  requiredEvents: string[];
  expectedUrls: string[];
  endpoints: Array<{
    id: string;
    url: string;
    status: string;
    matchesThisApp: boolean;
    missingEvents: string[];
  }>;
  /** True when at least one endpoint for this app is enabled and subscribed to every required event. */
  ok: boolean;
}

/**
 * Tenant browser → server: the Stripe.js `confirmPayment` result status. Advisory only — the server
 * applies `processing` / `requires_action` FORWARD-only and the webhook overwrites it with truth.
 */
export const paymentAttemptConfirmResultSchema = z.object({
  status: z.string().trim().min(1).max(64),
});
export type PaymentAttemptConfirmResultInput = z.infer<typeof paymentAttemptConfirmResultSchema>;

export const paymentAttemptListQuerySchema = z.object({
  leaseId: z.string().trim().min(1).max(128).optional(),
  tenantId: z.string().trim().min(1).max(128).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});
export type PaymentAttemptListQuery = z.infer<typeof paymentAttemptListQuerySchema>;
